import { clientWithApiClient } from "../../client";
import type { ApiClient } from "../../types";
import { appendRegionIds } from "../regions/params";

import type { Response } from "../../types";
import type {
  Category,
  CategoriesListResponse,
  CategoryWithDetails,
  CreateCategoryRequest,
  UpdateCategoryRequest,
  AssignPhotoRequest,
  ReorderPhotosRequest,
  UpdatePhotoCaptionRequest,
  LibraryPhoto,
  ListCategoriesParams,
  GetCategoryParams,
  VehicleOptionsParams,
  VehicleOptionsResponse,
  BulkVehiclesRequest,
} from "./types";

const list = (
  apiClient: ApiClient,
  fleetId: string,
  params?: ListCategoriesParams,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  if (params?.page) {
    searchParams.append("page", params.page.toString());
  }

  if (params?.per_page) {
    searchParams.append("per_page", params.per_page.toString());
  }

  if (params?.search) {
    searchParams.append("search", params.search);
  }

  if (params?.filter) {
    searchParams.append("filter", params.filter);
  }

  if (params?.sort) {
    searchParams.append("sort", params.sort);
  }

  appendRegionIds(searchParams, params?.region_ids);

  return clientWithApiClient<CategoriesListResponse>(
    apiClient,
    `/categories?${searchParams.toString()}`,
  );
};

const get = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  params?: GetCategoryParams,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  if (params?.vehicle_status) {
    searchParams.append("vehicle_status", params.vehicle_status);
  }

  return clientWithApiClient<Response<CategoryWithDetails>>(
    apiClient,
    `/categories/${id}?${searchParams.toString()}`,
  );
};

/**
 * Every vehicle the caller may put in a category, region-scoped, with whether
 * it is already in `category_id` and which other categories hold it. Leave
 * `category_id` out when the category does not exist yet.
 */
const vehicleOptions = (
  apiClient: ApiClient,
  fleetId: string,
  params?: VehicleOptionsParams,
) => {
  const searchParams = new URLSearchParams();
  searchParams.append("fleet_id", fleetId);

  if (params?.category_id) {
    searchParams.append("category_id", params.category_id);
  }

  return clientWithApiClient<VehicleOptionsResponse>(
    apiClient,
    `/categories/vehicle-options?${searchParams.toString()}`,
  );
};

// The FMS API finds the category inside the fleet it reads from `fleet_id`;
// without it, it uses one of the user's fleets on its own.
const fleetPath = (path: string, fleetId: string) =>
  `${path}?fleet_id=${encodeURIComponent(fleetId)}`;

const create = (
  apiClient: ApiClient,
  fleetId: string,
  category: CreateCategoryRequest,
) =>
  clientWithApiClient<Response<Category>>(
    apiClient,
    fleetPath("/categories", fleetId),
    {
      method: "POST",
      body: JSON.stringify({ category }),
    },
  );

const update = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  category: UpdateCategoryRequest,
) =>
  clientWithApiClient<Response<Category>>(
    apiClient,
    fleetPath(`/categories/${id}`, fleetId),
    {
      method: "PUT",
      body: JSON.stringify({ category }),
    },
  );

const remove = (apiClient: ApiClient, fleetId: string, id: string) =>
  clientWithApiClient(apiClient, fleetPath(`/categories/${id}`, fleetId), {
    method: "DELETE",
  });

/** Adds and removes vehicles in one request: all of it or none of it. */
const bulkVehicles = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  request: BulkVehiclesRequest,
) =>
  clientWithApiClient<Response<CategoryWithDetails>>(
    apiClient,
    fleetPath(`/categories/${id}/vehicles/bulk`, fleetId),
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

const assignPhoto = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  request: AssignPhotoRequest,
) =>
  clientWithApiClient(
    apiClient,
    fleetPath(`/categories/${id}/photos`, fleetId),
    {
      method: "POST",
      body: JSON.stringify(request),
    },
  );

/**
 * Uploads one image to the fleet's photo library (multipart: `photo[attachment]`
 * and `photo[name]`). It is not on a category until `assignPhoto` puts it there.
 */
const uploadLibraryPhoto = (apiClient: ApiClient, formData: FormData) =>
  clientWithApiClient<Response<LibraryPhoto>>(apiClient, "/photos", {
    method: "POST",
    body: formData,
  });

/**
 * Removes a photo from the fleet's photo library (`DELETE /photos/:id`): for
 * an upload that never got assigned to anything.
 */
const deleteLibraryPhoto = (apiClient: ApiClient, photoId: string) =>
  clientWithApiClient(apiClient, `/photos/${photoId}`, { method: "DELETE" });

/** Renames a photo on one category only; a blank caption clears it. */
const updatePhotoCaption = (
  apiClient: ApiClient,
  fleetId: string,
  categoryId: string,
  photoId: string,
  request: UpdatePhotoCaptionRequest,
) =>
  clientWithApiClient(
    apiClient,
    fleetPath(`/categories/${categoryId}/photos/${photoId}`, fleetId),
    {
      method: "PATCH",
      body: JSON.stringify(request),
    },
  );

const removePhoto = (
  apiClient: ApiClient,
  fleetId: string,
  categoryId: string,
  photoId: string,
) =>
  clientWithApiClient(
    apiClient,
    fleetPath(`/categories/${categoryId}/photos/${photoId}`, fleetId),
    {
      method: "DELETE",
    },
  );

const reorderPhotos = (
  apiClient: ApiClient,
  fleetId: string,
  id: string,
  request: ReorderPhotosRequest,
) =>
  clientWithApiClient(
    apiClient,
    fleetPath(`/categories/${id}/photos/reorder`, fleetId),
    {
      method: "PUT",
      body: JSON.stringify(request),
    },
  );

export const categories = {
  list,
  get,
  vehicleOptions,
  create,
  update,
  delete: remove,
  bulkVehicles,
  assignPhoto,
  uploadLibraryPhoto,
  deleteLibraryPhoto,
  updatePhotoCaption,
  removePhoto,
  reorderPhotos,
};
